import React, { useEffect, useState } from 'react';
import { Connection, ConnectionSource, ConnectionState } from '../types';
import { toastError } from 'bench-internal-theme';

import styled from 'styled-components';
import ConnectionCardHeader from './ConnectionCardHeader';
import statesToMessages from './statesToMessages';
import SquareLocationTable from './SquareLocationTable';
import LedgerTable from './LedgerTable';
import ErrorBanner from './ErrorBanner';

import EditButton from './buttons/EditButton';
import ResetButton from './buttons/ResetButton';
import DeleteButton from './buttons/DeleteButton';
import SyncButton from './buttons/SyncButton';
import PreviewButton from './buttons/PreviewButton';
import { useQuery } from '@apollo/client';
import { GET_MIN_MAX_TRANSACTION_DATE } from '../queries';
import { useClient } from '../../ClientContext';

const CardLayout = styled.div`
  display: grid;
  gap: 8px;
  border: 1px solid #c6c6c6;
  padding: 20px;
  margin: 10px 0;
  border-radius: 8px;
  text-align: left;
`;

const ActionButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
`;

type ConnectionCardProps = {
  connection: Connection;
};

const ConnectionCard = ({ connection }: ConnectionCardProps) => {
  const isSquareConnection = connection.source === ConnectionSource[ConnectionSource.SQUARE];
  const hasAccounts = connection.accounts.length > 0;
  const [filteredConnection, setFilteredConnection] = useState<Connection | null>();

  const connectionWithNoAccountErrorMessage = () => {
    // handles the case for PayPal/Amazon connections where sync have happened but no transactions are fetched
    if (
      (connection.source === ConnectionSource[ConnectionSource.AMAZON] ||
        connection.source === ConnectionSource[ConnectionSource.PAYPAL]) &&
      connection.earliestSyncDate
    )
      return false;
    if (!connection.accounts || !connection.accounts.length) {
      return 'Plaid could not find any deposit or credit card account for this connection. Confirm with your clients that they have at least one saving, checking or credit card account associated with this online banking login. Contact Bench App Support if your client has one of these accounts but this error persists.';
    }
  };

  const hasStateErrors =
    connection.state !== ConnectionState[ConnectionState.CONNECTED] ||
    connectionWithNoAccountErrorMessage();

  const commonPayPalAndAmazonMessages = {
    [ConnectionState[ConnectionState.NOT_READY]]:
      'The connection is not ready. Please check after some time.',
    [ConnectionState[ConnectionState.CONNECTION_NEEDS_UPDATE]]:
      'This account is no longer connected. Ask the customer to reconnect via the Connections page.',
    [ConnectionState[ConnectionState.CONNECTION_DISABLED]]:
      'This account’s connection has been disabled. This error can happen when the customer needs to take additional steps to resolve their account. Ask the customer to check their account and delete the account as needed to reattempt the connection.',
    [ConnectionState[ConnectionState.CONNECTION_LINK_ERROR]]:
      'An error occurred for this connection. Ask the customer to reconnect via the Connections page.',
    [ConnectionState[ConnectionState.CONNECTION_ERROR]]:
      'The account was authenticated but there is a problem connecting or syncing data. Inform the customer and delete the account as needed to reattempt the connection.',
    [ConnectionState[ConnectionState.CONNECTED]]:
      'This account has not yet been synced. Please click sync and enter the initial sync date',
  };

  const sourceSpecificStatesToMessages = {
    STRIPE: {
      [ConnectionState[ConnectionState.MISSING_PERMISSIONS]]:
        'This connection is missing required permissions from Stripe. Please ask your client to visit (or install) the Bench Stripe App in their Stripe dashboard and accept the permissions.',
    },
    PAYPAL: commonPayPalAndAmazonMessages,
    AMAZON: {
      ...commonPayPalAndAmazonMessages,
      [ConnectionState[ConnectionState.CONNECTED]]:
        'This account has not yet been synced. The first sync will retrieve two full months of data.  ',
    },
  };

  const hasAccountsMappedToLedgers =
    connection.accounts?.filter(account => account?.ledger).length > 0;

  const client = useClient();
  const { data, loading, error } = useQuery(GET_MIN_MAX_TRANSACTION_DATE, {
    variables: { clientId: client.id, source: connection.source },
  });

  useEffect(() => {
    if (error) {
      setFilteredConnection(null);
      toastError(
        'Connection',
        `There was an error fetching ${connection.source} data. Please refresh the page.`,
      );
    } else if (!loading && data) {
      const filteredConnectionData = data.financialConnections.find(c => c.id === connection.id);
      setFilteredConnection(filteredConnectionData);
    }
  }, [loading, data, error, connection]);

  const hasTransactions = () => {
    if (!filteredConnection) return false;
    const transactions = filteredConnection.accounts
      .map(account => account)
      .map(account => account.latestTransactions.data);
    return transactions.flat().length > 0;
  };

  const stateErrorMessage = () => {
    return (
      sourceSpecificStatesToMessages[connection.source]?.[connection.state] ||
      statesToMessages[connection.state] ||
      connectionWithNoAccountErrorMessage() ||
      'Please contact Bench support for assistance.'
    );
  };

  const hasMessageActionState = [
    ConnectionState[ConnectionState.INVALID_CREDENTIALS],
    ConnectionState[ConnectionState.INVALID_MFA],
    ConnectionState[ConnectionState.ITEM_LOCKED],
    ConnectionState[ConnectionState.ITEM_LOGIN_REQUIRED],
    ConnectionState[ConnectionState.ITEM_NOT_SUPPORTED],
    ConnectionState[ConnectionState.USER_SETUP_REQUIRED],
    ConnectionState[ConnectionState.MFA_NOT_SUPPORTED],
    ConnectionState[ConnectionState.NO_ACCOUNTS],
    ConnectionState[ConnectionState.REMOVED_FROM_SOURCE],
  ].includes(connection.state);

  return (
    <div>
      <CardLayout>
        <ConnectionCardHeader connection={connection} />
        {hasStateErrors && (
          <ErrorBanner>
            <p>
              {stateErrorMessage()}{' '}
              {hasMessageActionState && <a href="/messages">Message Client</a>}
            </p>
          </ErrorBanner>
        )}
        <ActionButtonWrapper>
          <SyncButton connection={connection} />
          <EditButton connectionId={connection.id} />
          <ResetButton connection={connection} />
          <DeleteButton connection={connection} />
          <PreviewButton connection={filteredConnection} />
        </ActionButtonWrapper>

        {isSquareConnection && hasAccounts && <SquareLocationTable connection={connection} />}
        {hasAccountsMappedToLedgers && <LedgerTable connection={connection} />}

        {!hasAccountsMappedToLedgers && hasAccounts && (
          <ErrorBanner color="#826628" backgroundColor="#fff5df">
            <p>
              You have not mapped any accounts. Click the edit button to link ledgers to accounts.
            </p>
          </ErrorBanner>
        )}
        {!loading && !hasTransactions() && (
          <ErrorBanner color="#826628" backgroundColor="#fff5df">
            <p>No transaction data exists to preview.</p>
          </ErrorBanner>
        )}
      </CardLayout>
    </div>
  );
};

export default ConnectionCard;
